import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import Page from 'layout/page'
import PageTitle from 'layout/page-title'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import AppBadge from 'shared/badges/app-badge'
import CenteredSpinner from 'shared/centered-spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import useMonitoringWorkflows from './use-monitoring-workflows'

const formatCount = (value) => (value || 0).toLocaleString()

// An empty queue gets a check, not "0 new", so it can be dismissed without reading.
const NewBadge = ({ count }) =>
  count > 0 ? (
    <AppBadge variant="primary" data-testid="workflow-new-count">
      {formatCount(count)} new
    </AppBadge>
  ) : (
    <AppBadge
      variant="success"
      data-testid="workflow-new-count"
      aria-label="Nothing new"
      title="Nothing new"
    >
      <FontAwesomeIcon icon={faCheck} />
    </AppBadge>
  )

NewBadge.propTypes = {
  count: PropTypes.number
}

const WorkflowCard = ({ workflow, onOpen }) => (
  <Card
    className="h-100 shadow-sm"
    role="button"
    tabIndex={0}
    data-testid={`workflow-card-${workflow.slug}`}
    aria-label={`Open ${workflow.title}`}
    style={{ cursor: 'pointer' }}
    onClick={() => onOpen(workflow)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onOpen(workflow)
      }
    }}
  >
    <Card.Body className="d-flex flex-column">
      <div className="d-flex align-items-start justify-content-between mb-1">
        <h5 className="mb-0">{workflow.title}</h5>
        {workflow.is_dormant && (
          <AppBadge variant="secondary" className="ms-2 flex-shrink-0">
            Not enabled
          </AppBadge>
        )}
      </div>
      <p className="text-muted small mb-3">{workflow.summary}</p>
      <div className="mt-auto">
        <NewBadge count={workflow.new_count} />
      </div>
    </Card.Body>
  </Card>
)

WorkflowCard.propTypes = {
  workflow: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired
}

// The catch-all spans every dataset, so it gets its own group, not a module's.
const CATCH_ALL_GROUP = 'All datasets'

// Server order puts the catch-all's group last.
const groupByModule = (workflows) =>
  workflows.reduce((groups, workflow) => {
    const name = workflow.module_name || CATCH_ALL_GROUP
    const group = groups.find((g) => g.name === name)

    if (group) {
      group.workflows.push(workflow)
    } else {
      groups.push({ name, workflows: [workflow] })
    }

    return groups
  }, [])

const MonitoringWorkflows = () => {
  const navigate = useNavigate()
  // No date range: a queue's backlog is the whole backlog. The catch-all's window is
  // server-side (MonitoringWorkflowsController::CATCH_ALL_WINDOW_DAYS).
  const { data, isLoading, isError } = useMonitoringWorkflows()

  const groups = groupByModule(data?.workflows || [])

  const openWorkflow = (workflow) => navigate(`/monitoring/${workflow.slug}`)

  const crumbs = [{ name: 'Monitoring', url: '' }]

  return (
    <Page title="Monitoring">
      <PageTitle breadcrumbs={crumbs} />

      <p className="text-muted">Pick a review to work through.</p>

      {isLoading && <CenteredSpinner />}

      {isError && (
        <Card className="shadow-sm">
          <Card.Body className="text-muted">
            Could not load the monitoring workflows.
          </Card.Body>
        </Card>
      )}

      {!isLoading &&
        !isError &&
        groups.map((group) => (
          <div key={group.name} className="mb-4">
            <div
              className="text-muted small fw-bold text-uppercase mb-2"
              data-testid="workflow-group"
            >
              {group.name}
            </div>
            <Row xs={1} md={2} xl={4} className="g-3">
              {group.workflows.map((workflow) => (
                <Col key={workflow.slug}>
                  <WorkflowCard workflow={workflow} onOpen={openWorkflow} />
                </Col>
              ))}
            </Row>
          </div>
        ))}

    </Page>
  )
}

export default MonitoringWorkflows
